/**
 * Footer Component
 * Application footer with links, social media, newsletter signup, and trust badges
 */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  TextField,
  Button,
  Stack,
  IconButton,
  Divider,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
  Lock as LockIcon,
  Verified as VerifiedIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/**
 * Footer component
 * Displays footer navigation, newsletter signup, and trust information
 */
const Footer = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isNewsletterOpen, setIsNewsletterOpen] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState('');

  // Handle newsletter signup
  const handleNewsletterSignup = async (e) => {
    e.preventDefault();
    if (!email) {
      setNewsletterMessage('Please enter a valid email address');
      return;
    }

    try {
      // TODO: Replace with actual API call
      // await api.newsletter.subscribe(email);
      setNewsletterMessage('Successfully subscribed to our newsletter!');
      setEmail('');
    } catch (error) {
      setNewsletterMessage('Failed to subscribe. Please try again.');
    }
    setIsNewsletterOpen(true);
  };

  // Footer link columns configuration
  const footerSections = [
    {
      title: 'Company',
      links: [
        { label: 'About Us', path: '/about' },
        { label: 'Blog', path: '/blog' },
        { label: 'Contact Us', path: '/contact' },
      ],
    },
    {
      title: 'Support',
      links: [
        { label: 'How It Works', path: '/how-it-works' },
        { label: 'Help Center', path: '/how-it-works' },
        { label: 'Contact Us', path: '/contact' },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Terms of Service', path: '/terms' },
        { label: 'Privacy Policy', path: '/privacy' },
      ],
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#1A1A2E',
        color: '#FFFFFF',
        pt: 6,
        pb: 3,
        mt: 8,
      }}
    >
      <Container maxWidth="lg">
        {/* Main Footer Content */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* Logo and Tagline */}
          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ mb: 2 }}>
              <img
                src="/tradewars-icon.svg"
                alt="TradeWars"
                style={{ height: '52px', width: 'auto' }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 2 }}>
              Empowering homeowners with competitive bidding and verified contractors.
            </Typography>

            {/* Social Media Icons */}
            <Stack direction="row" spacing={1}>
              <IconButton
                size="small"
                component="a"
                href="https://www.facebook.com/profile.php?id=61570820211895"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#AAAAAA',
                  '&:hover': { color: '#B71C1C' },
                }}
              >
                <FacebookIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                component="a"
                href="https://twitter.com/tradewars"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#AAAAAA',
                  '&:hover': { color: '#B71C1C' },
                }}
              >
                <TwitterIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                component="a"
                href="https://linkedin.com/company/tradewars"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#AAAAAA',
                  '&:hover': { color: '#B71C1C' },
                }}
              >
                <LinkedInIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                component="a"
                href="https://instagram.com/tradewars"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#AAAAAA',
                  '&:hover': { color: '#B71C1C' },
                }}
              >
                <InstagramIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Grid>

          {/* Footer Sections */}
          {footerSections.map((section) => (
            <Grid item xs={6} sm={3} md={2.5} key={section.title}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: '#FFFFFF',
                  fontSize: '0.95rem',
                }}
              >
                {section.title}
              </Typography>
              <Stack spacing={1}>
                {section.links.map((link) => (
                  <Link
                    key={link.label}
                    component="button"
                    onClick={() => {
                      if (link.path.startsWith('/')) {
                        navigate(link.path);
                      }
                    }}
                    sx={{
                      color: '#AAAAAA',
                      fontSize: '0.875rem',
                      textDecoration: 'none',
                      transition: 'color 0.3s',
                      '&:hover': {
                        color: '#B71C1C',
                      },
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </Stack>
            </Grid>
          ))}

          {/* Newsletter Signup */}
          <Grid item xs={12} sm={6} md={2.5}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: '#FFFFFF',
                fontSize: '0.95rem',
              }}
            >
              Newsletter
            </Typography>
            <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 2 }}>
              Subscribe for tips and updates on home remodeling.
            </Typography>
            <Box
              component="form"
              onSubmit={handleNewsletterSignup}
              sx={{ display: 'flex', gap: 1 }}
            >
              <TextField
                size="small"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: '#B71C1C' },
                    '&.Mui-focused fieldset': { borderColor: '#B71C1C' },
                  },
                  '& .MuiOutlinedInput-input::placeholder': { color: '#AAAAAA' },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: '#FF6B35',
                  color: '#FFFFFF',
                  '&:hover': { backgroundColor: '#E55100' },
                }}
              >
                Join
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 4 }} />

        {/* Trust Badges */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={4} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockIcon sx={{ color: '#FF6B35', fontSize: '1.5rem' }} />
              <Typography variant="body2" sx={{ color: '#AAAAAA' }}>
                SSL Secure
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShieldIcon sx={{ color: '#C9A84C', fontSize: '1.5rem' }} />
              <Typography variant="body2" sx={{ color: '#AAAAAA' }}>
                Escrow Protected
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VerifiedIcon sx={{ color: '#4CAF50', fontSize: '1.5rem' }} />
              <Typography variant="body2" sx={{ color: '#AAAAAA' }}>
                Verified Contractors
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />

        {/* Help button + Copyright */}
        <Box sx={{ textAlign: 'center' }}>
          <Button
            onClick={() => navigate('/how-it-works')}
            variant="outlined"
            size="small"
            sx={{
              mb: 2,
              borderColor: 'rgba(255,255,255,0.25)',
              color: 'rgba(255,255,255,0.7)',
              borderRadius: 99,
              px: 3,
              fontSize: '0.8rem',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { borderColor: '#C9A84C', color: '#C9A84C' },
            }}
          >
            Help &amp; How It Works
          </Button>
          <Typography variant="body2" sx={{ color: '#666666' }}>
            © 2026 TradeWars. All rights reserved. | Tampa, Florida
          </Typography>
        </Box>
      </Container>

      {/* Newsletter Snackbar */}
      <Snackbar
        open={isNewsletterOpen}
        autoHideDuration={6000}
        onClose={() => setIsNewsletterOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setIsNewsletterOpen(false)}
          severity={newsletterMessage.includes('successfully') ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {newsletterMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Footer;
